import React, { useEffect, useState } from 'react';
import FormShell from './FormShell';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { loadSavedForm, saveForm } from './formUtils';

const emptyDirector = () => ({
  _uid: crypto.randomUUID(),
  fullName: '',
  idNumber: '',
  email: '',
  phone: '',
  nationality: 'South African',
  residentialAddress: '',
});

const DEFAULTS = {
  npcType: 'standard',
  hasMembers: 'no',
  proposedName1: '',
  proposedName2: '',
  proposedName3: '',
  mainObjective: '',
  physicalAddress: '',
  postalAddress: '',
  sameAsPhysical: false,
  directors: [emptyDirector(), emptyDirector(), emptyDirector()],
  publicBenefit: '',
  preferredContactMethod: 'email',
};

const steps = ['NPC Type', 'Names & Objective', 'Address', 'Directors', 'Review'];

const NpcRegistrationForm = ({ serviceId, onComplete }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => loadSavedForm(serviceId) || DEFAULTS);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    saveForm(serviceId, form);
    setSaved(true);
    const t = setTimeout(() => setSaved(false), 1500);
    return () => clearTimeout(t);
  }, [serviceId, form]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const updateDir = (i, field, value) =>
    setForm((p) => ({
      ...p,
      directors: p.directors.map((d, idx) => (idx === i ? { ...d, [field]: value } : d)),
    }));
  const addDir = () => setForm((p) => ({ ...p, directors: [...p.directors, emptyDirector()] }));
  const removeDir = (i) =>
    setForm((p) => ({ ...p, directors: p.directors.filter((_, idx) => idx !== i) }));

  const validate = () => {
    switch (step) {
      case 2:
        if (!form.proposedName1 || !form.mainObjective) {
          toast({
            title: 'Required Fields Missing',
            description: 'Provide a proposed name and the main objective.',
            variant: 'destructive',
          });
          return false;
        }
        break;
      case 3:
        if (!form.physicalAddress) {
          toast({
            title: 'Required Fields Missing',
            description: 'Physical address is required.',
            variant: 'destructive',
          });
          return false;
        }
        break;
      case 4:
        if (form.directors.length < 3) {
          toast({
            title: 'Minimum 3 directors required',
            description: 'An NPC must have at least 3 directors.',
            variant: 'destructive',
          });
          return false;
        }
        if (form.directors.some((d) => !d.fullName || !d.idNumber || !d.email)) {
          toast({
            title: 'Required Fields Missing',
            description: 'Complete name, ID and email for every director.',
            variant: 'destructive',
          });
          return false;
        }
        break;
      default:
        break;
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    setStep((s) => Math.min(s + 1, steps.length));
    window.scrollTo(0, 0);
  };
  const prev = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };
  const submit = () => {
    if (!validate()) return;
    onComplete(form);
  };

  return (
    <FormShell
      title="NPC Registration Application"
      stepLabels={steps}
      currentStep={step}
      onNext={next}
      onPrevious={prev}
      onSubmit={submit}
      autoSaved={saved}
    >
      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">NPC Type</h3>
          <div>
            <Label>NPC Variant</Label>
            <select
              value={form.npcType}
              onChange={(e) => set('npcType', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="standard">Standard NPC (faster)</option>
              <option value="bespoke">Bespoke NPC (custom MOI)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Standard NPCs use the default CIPC MOI; bespoke NPCs use a custom MOI tailored
              to your governance.
            </p>
          </div>
          <div>
            <Label>Will the NPC have members?</Label>
            <select
              value={form.hasMembers}
              onChange={(e) => set('hasMembers', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="no">No (board-managed only)</option>
              <option value="yes">Yes (members + board)</option>
            </select>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Names &amp; Objective</h3>
          <p className="text-sm text-gray-600">
            Provide up to 3 proposed names. CIPC will check availability in order. Names
            should reflect a non-profit purpose.
          </p>
          <div>
            <Label>1st Choice Name *</Label>
            <Input value={form.proposedName1} onChange={(e) => set('proposedName1', e.target.value)} />
          </div>
          <div>
            <Label>2nd Choice Name</Label>
            <Input value={form.proposedName2} onChange={(e) => set('proposedName2', e.target.value)} />
          </div>
          <div>
            <Label>3rd Choice Name</Label>
            <Input value={form.proposedName3} onChange={(e) => set('proposedName3', e.target.value)} />
          </div>
          <div>
            <Label>Main Objective *</Label>
            <textarea
              className="mt-1 w-full p-2 border border-gray-300 rounded-md min-h-[100px]"
              value={form.mainObjective}
              onChange={(e) => set('mainObjective', e.target.value)}
              placeholder="What public benefit will the NPC pursue?"
            />
          </div>
          <div>
            <Label>Public Benefit Activity (optional)</Label>
            <Input
              value={form.publicBenefit}
              onChange={(e) => set('publicBenefit', e.target.value)}
              placeholder="e.g. Education, Health, Welfare"
            />
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Registered Address</h3>
          <div>
            <Label>Physical Address *</Label>
            <textarea
              className="mt-1 w-full p-2 border border-gray-300 rounded-md min-h-[80px]"
              value={form.physicalAddress}
              onChange={(e) => set('physicalAddress', e.target.value)}
              placeholder="Street, Suburb, City, Province, Postal Code"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="sameAddr"
              checked={form.sameAsPhysical}
              onChange={(e) => {
                const checked = e.target.checked;
                set('sameAsPhysical', checked);
                if (checked) set('postalAddress', form.physicalAddress);
              }}
              className="h-4 w-4 text-emerald-600"
            />
            <Label htmlFor="sameAddr" className="cursor-pointer">
              Postal address same as physical
            </Label>
          </div>
          {!form.sameAsPhysical && (
            <div>
              <Label>Postal Address</Label>
              <textarea
                className="mt-1 w-full p-2 border border-gray-300 rounded-md min-h-[80px]"
                value={form.postalAddress}
                onChange={(e) => set('postalAddress', e.target.value)}
              />
            </div>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Directors</h3>
          <p className="text-sm text-gray-600">An NPC requires a minimum of 3 directors.</p>
          {form.directors.map((d, i) => (
            <Card key={d._uid || `npc-dir-${i}`}>
              <CardHeader className="bg-emerald-50">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base">Director {i + 1}</CardTitle>
                  {form.directors.length > 3 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDir(i)}
                      className="text-red-600"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                <div>
                  <Label>Full Name *</Label>
                  <Input value={d.fullName} onChange={(e) => updateDir(i, 'fullName', e.target.value)} />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label>ID / Passport *</Label>
                    <Input value={d.idNumber} onChange={(e) => updateDir(i, 'idNumber', e.target.value)} />
                  </div>
                  <div>
                    <Label>Nationality</Label>
                    <select
                      value={d.nationality}
                      onChange={(e) => updateDir(i, 'nationality', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      <option>South African</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={d.email}
                      onChange={(e) => updateDir(i, 'email', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={d.phone} onChange={(e) => updateDir(i, 'phone', e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label>Residential Address</Label>
                  <Input
                    value={d.residentialAddress}
                    onChange={(e) => updateDir(i, 'residentialAddress', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
          <Button
            onClick={addDir}
            variant="outline"
            className="w-full border-dashed border-2 border-emerald-300 text-emerald-600 hover:bg-emerald-50"
          >
            + Add Another Director
          </Button>
        </div>
      )}

      {step === 5 && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-emerald-800">Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><strong>Type:</strong> {form.npcType} (members: {form.hasMembers})</p>
            <p><strong>1st Name:</strong> {form.proposedName1}</p>
            <p><strong>Directors:</strong> {form.directors.length}</p>
            <p><strong>Objective:</strong> {form.mainObjective.slice(0, 120)}{form.mainObjective.length > 120 ? '…' : ''}</p>
          </CardContent>
        </Card>
      )}
    </FormShell>
  );
};

export default NpcRegistrationForm;
